import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Tooltip,
  Paper,
  alpha,
  useTheme,
  Chip
} from "@mui/material";
import apiService from "../services/api";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

interface Usage {
  monthlyLimit: number;
  extraPages?: number;
  totalAvailable?: number;
  pagesUsed: number;
  pagesReserved?: number;
  pagesRemaining: number;
  daysRemaining?: number;
  isInGracePeriod?: boolean;
  gracePeriodRemaining?: number;
  isExpired?: boolean;
  isActive?: boolean;
  warningLevel?: "none" | "warning" | "critical";
  nextResetDate?: string;
}

const getStatusLabel = (
  isActive: boolean,
  isExpired: boolean,
  isInGracePeriod: boolean
): string => {
  if (!isActive) return "Inactive";
  if (isExpired) return "Expired";
  if (isInGracePeriod) return "Grace Period";
  return "Active";
};

const getStatusColor = (
  isActive: boolean,
  isExpired: boolean,
  isInGracePeriod: boolean
): "success" | "warning" | "error" | "default" => {
  if (!isActive) return "error";
  if (isExpired) return "error";
  if (isInGracePeriod) return "warning";
  return "success";
};

const UsageIndicator: React.FC = () => {
  const theme = useTheme();
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUsage = async () => {
    try {
      const res = await apiService.getSubscription();

      setUsage({
        monthlyLimit: res.monthlyLimit,
        extraPages: res.extraPages,
        totalAvailable: res.totalAvailable,
        pagesUsed: res.pagesUsed,
        pagesReserved: res.pagesReserved,
        pagesRemaining: res.pagesRemaining,
        daysRemaining: res.daysRemaining,
        isInGracePeriod: res.isInGracePeriod,
        gracePeriodRemaining: res.gracePeriodRemaining,
        isExpired: res.isExpired,
        isActive: res.isActive,
        warningLevel: res.warningLevel,
        nextResetDate: res.nextResetDate || undefined
      });
    } catch (err) {
      console.error("Usage load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsage();

    const interval = setInterval(loadUsage, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const metrics = useMemo(() => {
    const monthlyLimit = Number(usage?.monthlyLimit ?? 0);
    const extraPages = Number(usage?.extraPages ?? 0);
    const totalAvailable = Number(
      usage?.totalAvailable ?? monthlyLimit + extraPages
    );
    const pagesUsed = Number(usage?.pagesUsed ?? 0);
    const pagesRemaining = Number(usage?.pagesRemaining ?? 0);
    const daysRemaining = usage?.daysRemaining ?? 0;
    const isInGracePeriod = usage?.isInGracePeriod ?? false;
    const isExpired = usage?.isExpired ?? false;
    const isActive = usage?.isActive ?? false;
    const gracePeriodRemaining = usage?.gracePeriodRemaining ?? 0;
    const warningLevel = usage?.warningLevel ?? "none";

    const percent =
      totalAvailable > 0
        ? Math.min((pagesUsed / totalAvailable) * 100, 100)
        : 0;

    const statusLabel = getStatusLabel(
      isActive,
      isExpired,
      isInGracePeriod
    );

    const statusColor = getStatusColor(
      isActive,
      isExpired,
      isInGracePeriod
    );

    return {
      monthlyLimit,
      extraPages,
      totalAvailable,
      pagesUsed,
      pagesRemaining,
      daysRemaining,
      isInGracePeriod,
      isExpired,
      isActive,
      gracePeriodRemaining,
      warningLevel,
      percent,
      statusLabel,
      statusColor
    };
  }, [usage]);

  const getProgressColor = () => {
    if (metrics.warningLevel === "critical") return theme.palette.error.main;
    if (metrics.warningLevel === "warning") return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getStatusIcon = () => {
    if (!metrics.isActive || metrics.isExpired) {
      return (
        <CancelIcon
          sx={{
            fontSize: 14,
            color: theme.palette.error.main,
            mr: 0.5
          }}
        />
      );
    }

    if (metrics.isInGracePeriod) {
      return (
        <WarningIcon
          sx={{
            fontSize: 14,
            color: theme.palette.warning.main,
            mr: 0.5
          }}
        />
      );
    }

    return (
      <CheckCircleIcon
        sx={{
          fontSize: 14,
          color: theme.palette.success.main,
          mr: 0.5
        }}
      />
    );
  };

  const getExpiryText = (): string => {
    if (!metrics.isActive) return "Subscription inactive - please renew";
    if (metrics.isExpired) return "⚠️ Subscription expired - please renew";

    if (metrics.isInGracePeriod) {
      return `⚠️ Grace period: ${metrics.gracePeriodRemaining} day${
        metrics.gracePeriodRemaining !== 1 ? "s" : ""
      } left to renew`;
    }

    if (metrics.daysRemaining === 0) {
      return "🔄 Expires today!";
    }

    if (metrics.daysRemaining === 1) {
      return "⚠️ Expires tomorrow";
    }

    if (metrics.daysRemaining > 0) {
      return `📅 Expires in ${metrics.daysRemaining} days`;
    }

    return "";
  };

  const getTooltipBackground = () => {
    if (!metrics.isActive || metrics.isExpired) {
      return "linear-gradient(135deg, #FFFFFF 0%, #FFF5F5 100%)";
    }

    if (metrics.isInGracePeriod || metrics.warningLevel === "warning") {
      return "linear-gradient(135deg, #FFFFFF 0%, #FFF8E6 100%)";
    }

    if (metrics.warningLevel === "critical") {
      return "linear-gradient(135deg, #FFFFFF 0%, #FFF5F5 100%)";
    }

    return "linear-gradient(135deg, #FFFFFF 0%, #EAF8F7 100%)";
  };

  const getTooltipBorderColor = () => {
    if (!metrics.isActive || metrics.isExpired) {
      return alpha(theme.palette.error.main, 0.28);
    }

    if (metrics.isInGracePeriod || metrics.warningLevel === "warning") {
      return alpha(theme.palette.warning.main, 0.28);
    }

    if (metrics.warningLevel === "critical") {
      return alpha(theme.palette.error.main, 0.28);
    }

    return alpha(theme.palette.primary.main, 0.22);
  };

  const getPaperBackground = () => {
    if (!metrics.isActive || metrics.isExpired) {
      return alpha(theme.palette.error.main, 0.06);
    }

    if (metrics.isInGracePeriod || metrics.warningLevel === "warning") {
      return alpha(theme.palette.warning.main, 0.08);
    }

    if (metrics.warningLevel === "critical") {
      return alpha(theme.palette.error.main, 0.06);
    }

    return alpha(theme.palette.primary.main, 0.04);
  };

  const getHoverBackground = () => {
    if (!metrics.isActive || metrics.isExpired) {
      return alpha(theme.palette.error.main, 0.09);
    }

    if (metrics.isInGracePeriod || metrics.warningLevel === "warning") {
      return alpha(theme.palette.warning.main, 0.12);
    }

    if (metrics.warningLevel === "critical") {
      return alpha(theme.palette.error.main, 0.09);
    }

    return alpha(theme.palette.primary.main, 0.08);
  };

  const getHoverBorderColor = () => {
    if (!metrics.isActive || metrics.isExpired) {
      return theme.palette.error.main;
    }

    if (metrics.isInGracePeriod || metrics.warningLevel === "warning") {
      return theme.palette.warning.main;
    }

    if (metrics.warningLevel === "critical") {
      return theme.palette.error.main;
    }

    return theme.palette.secondary.main;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
        <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!usage) return null;

  const expiryText = getExpiryText();

  return (
    <Tooltip
      title={
        <Box
          sx={{
            p: 1.5,
            minWidth: 240,
            background: getTooltipBackground(),
            borderRadius: 2,
            backdropFilter: "blur(20px)",
            border: `1px solid ${getTooltipBorderColor()}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: theme.palette.text.primary }}
            >
              <span style={{ color: "#214F80" }}>Extract</span>
              <span style={{ color: "#C6602F" }}>Fast</span>{" "}
              <span>Usage</span>
            </Typography>

            <Chip
              label={metrics.statusLabel}
              color={metrics.statusColor}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 600
              }}
            />
          </Box>

          <Typography
            variant="caption"
            display="block"
            sx={{ color: alpha(theme.palette.text.primary, 0.85), mb: 0.4 }}
          >
            Base Quota: {metrics.monthlyLimit} pages
          </Typography>

          {metrics.extraPages > 0 && (
            <Typography
              variant="caption"
              display="block"
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 600,
                mb: 0.4
              }}
            >
              Extra Pages: +{metrics.extraPages}
            </Typography>
          )}

          <Typography
            variant="caption"
            display="block"
            sx={{
              color: alpha(theme.palette.text.primary, 0.85),
              mb: 0.4
            }}
          >
            Used: {metrics.pagesUsed} • Remaining: {metrics.pagesRemaining}
          </Typography>

          {expiryText && (
            <Typography
              variant="caption"
              display="block"
              sx={{
                color:
                  !metrics.isActive || metrics.isExpired
                    ? theme.palette.error.main
                    : metrics.isInGracePeriod
                      ? theme.palette.warning.main
                      : theme.palette.text.primary,
                fontWeight: 600,
                mt: 0.5
              }}
            >
              {expiryText}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "transparent",
            p: 0,
            boxShadow: "none"
          }
        },
        arrow: {
          sx: {
            color:
              !metrics.isActive || metrics.isExpired
                ? "#FFF5F5"
                : metrics.isInGracePeriod || metrics.warningLevel === "warning"
                  ? "#FFF8E6"
                  : "#EAF8F7"
          }
        }
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          borderRadius: 2,
          borderColor: metrics.isInGracePeriod
            ? alpha(theme.palette.warning.main, 0.5)
            : !metrics.isActive || metrics.isExpired
              ? alpha(theme.palette.error.main, 0.5)
              : metrics.warningLevel === "critical"
                ? alpha(theme.palette.error.main, 0.35)
                : alpha(theme.palette.primary.main, 0.18),
          cursor: "pointer",
          transition: "all 0.2s ease",
          bgcolor: getPaperBackground(),
          backdropFilter: "blur(2px)",
          "&:hover": {
            borderColor: getHoverBorderColor(),
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
            bgcolor: getHoverBackground()
          }
        }}
      >
        <Box sx={{ minWidth: 180 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {getStatusIcon()}

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary
                }}
              >
                {metrics.pagesUsed} / {metrics.totalAvailable} pages
              </Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: getProgressColor()
              }}
            >
              {Math.round(metrics.percent)}%
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={metrics.percent}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: alpha(getProgressColor(), 0.1),
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
                background: `linear-gradient(90deg, ${getProgressColor()} 0%, ${
                  metrics.warningLevel === "critical"
                    ? theme.palette.error.light
                    : metrics.warningLevel === "warning"
                      ? theme.palette.warning.light
                      : theme.palette.success.light
                } 100%)`
              }
            }}
          />

          {!metrics.isExpired &&
            !metrics.isInGracePeriod &&
            metrics.isActive &&
            metrics.daysRemaining > 0 &&
            metrics.daysRemaining <= 7 && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  color: theme.palette.warning.main,
                  display: "block",
                  mt: 0.5,
                  fontWeight: 600
                }}
              >
                Expires in {metrics.daysRemaining} days
              </Typography>
            )}

          {metrics.isInGracePeriod && (
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                color: theme.palette.warning.main,
                display: "block",
                mt: 0.5,
                fontWeight: 600
              }}
            >
              Grace period: {metrics.gracePeriodRemaining} day
              {metrics.gracePeriodRemaining !== 1 ? "s" : ""} left
            </Typography>
          )}

          {(!metrics.isActive || metrics.isExpired) &&
            !metrics.isInGracePeriod && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  color: theme.palette.error.main,
                  display: "block",
                  mt: 0.5,
                  fontWeight: 600
                }}
              >
                {metrics.isExpired
                  ? "Expired - please renew"
                  : "Inactive - please renew"}
              </Typography>
            )}
        </Box>
      </Paper>
    </Tooltip>
  );
};

export default UsageIndicator;